// Waste bands in percent; `moderate` is the floor of the yellow band, `high` of
// the red. Exported so the column's legend can't drift from the cell colors.
export const WASTE_THRESHOLDS = {
  moderate: 10,
  high: 25,
};

// null for anything that isn't a real number. Number(null) and Number('') are
// both 0, so an absent figure would otherwise render as a confident "0.0%".
const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export function totalInfusions(rows) {
  return (rows || []).reduce((sum, row) => sum + (toNumber(row.infusions) || 0), 0);
}

export function percentOfTotal(infusions, total) {
  const count = toNumber(infusions) || 0;
  const denominator = toNumber(total) || 0;
  if (denominator <= 0) return '0.0';
  return ((count / denominator) * 100).toFixed(1);
}

export function formatFlowRate(rate) {
  const value = toNumber(rate);
  if (value === null) return 'N/A';
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

export function wasteClass(waste) {
  const value = toNumber(waste);
  if (value === null) return '';
  if (value >= WASTE_THRESHOLDS.high) return 'waste-high';
  if (value >= WASTE_THRESHOLDS.moderate) return 'waste-medium';
  return 'waste-low';
}

export function wasteDisplay(waste) {
  const value = toNumber(waste);
  if (value === null) return 'N/A';
  return `${value.toFixed(1)}%`;
}

/**
 * Average waste is infusion-weighted, not a mean of the per-row percentages: a
 * formulation given twice must not weigh as heavily as one given ten thousand
 * times. Rows with no waste figure are excluded from both halves of the ratio
 * rather than counted as zero, which would understate it.
 *
 * TEMPORARY: only the fallback for a `/formulations` response that carries no
 * `summary` block — the endpoint owns these aggregates (see
 * INVESTIGATE_DATA_CONTRACTS.md #2), because a client-side sum describes the
 * rows on screen rather than the set the user filtered to. Goes away with the
 * store's fallback. Its output must keep matching the store's `shapeSummary`,
 * or the footer would change meaning depending on which path produced it.
 */
export function buildSummary(rows) {
  const list = rows || [];
  const total = totalInfusions(list);

  let weightedWaste = 0;
  let weightedInfusions = 0;
  list.forEach((row) => {
    const waste = toNumber(row.waste);
    if (waste === null) return;
    const infusions = toNumber(row.infusions) || 0;
    weightedWaste += waste * infusions;
    weightedInfusions += infusions;
  });

  const minRates = list.map((row) => toNumber(row.min_rate)).filter((rate) => rate !== null);
  const maxRates = list.map((row) => toNumber(row.max_rate)).filter((rate) => rate !== null);

  return {
    totalInfusions: total,
    // 'N/A', not '0.0', when no row carried a waste figure: a zero there reads
    // as a measured zero. Matches shapeSummary's handling of a null
    // `averageWastePct`.
    averageWaste: weightedInfusions > 0 ? (weightedWaste / weightedInfusions).toFixed(1) : 'N/A',
    minFlowRate: minRates.length ? formatFlowRate(Math.min(...minRates)) : 'N/A',
    maxFlowRate: maxRates.length ? formatFlowRate(Math.max(...maxRates)) : 'N/A',
  };
}

export default buildSummary;
